'use client'

import { useState, useCallback, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import TransparentCheckout from '@/components/ui/transparent-checkout'

interface BookCheckoutFormProps {
  userEmail: string | null
  userName?: string | null
  userPhone?: string | null
  userDocument?: string | null
}

type FormStep = 'form' | 'processing' | 'success' | 'error'
type CheckoutSection = 'personal' | 'address' | 'payment'

// Step Indicator
function StepIndicator({
  activeSection,
  completedSections,
}: {
  activeSection: CheckoutSection
  completedSections: Set<CheckoutSection>
}) {
  const steps: { key: CheckoutSection; label: string; num: string }[] = [
    { key: 'personal', label: 'Dados', num: '1' },
    { key: 'address', label: 'Endereço', num: '2' },
    { key: 'payment', label: 'Pagamento', num: '3' },
  ]

  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {steps.map((step, i) => {
        const isCompleted = completedSections.has(step.key)
        const isActive = activeSection === step.key
        return (
          <div key={step.key} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500 ${
                  isCompleted
                    ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                    : isActive
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30 scale-110'
                    : 'bg-stone-200 text-stone-400'
                }`}
              >
                {isCompleted ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : (
                  step.num
                )}
              </div>
              <span
                className={`text-xs mt-1.5 font-medium transition-colors duration-300 ${
                  isCompleted ? 'text-green-600' : isActive ? 'text-blue-600' : 'text-stone-400'
                }`}
              >
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`w-12 sm:w-20 h-0.5 mx-1 sm:mx-2 -mt-5 transition-colors duration-500 ${
                  completedSections.has(steps[i].key) ? 'bg-green-400' : 'bg-stone-200'
                }`}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// Section Header
function SectionHeader({
  title,
  icon,
  isActive,
  isCompleted,
  summary,
  onEdit,
}: {
  title: string
  icon: React.ReactNode
  isActive: boolean
  isCompleted: boolean
  summary?: string
  onEdit?: () => void
}) {
  return (
    <div
      className={`flex items-center justify-between p-4 rounded-xl transition-all duration-300 ${
        isActive
          ? 'bg-blue-50 border-2 border-blue-200'
          : isCompleted
          ? 'bg-green-50 border-2 border-green-200'
          : 'bg-stone-50 border-2 border-stone-200 opacity-60'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            isCompleted ? 'bg-green-500 text-white' : isActive ? 'bg-blue-600 text-white' : 'bg-stone-300 text-white'
          }`}
        >
          {isCompleted ? (
            <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          ) : (
            icon
          )}
        </div>
        <div>
          <h4
            className={`text-sm font-semibold ${
              isCompleted ? 'text-green-800' : isActive ? 'text-blue-900' : 'text-stone-500'
            }`}
          >
            {title}
          </h4>
          {isCompleted && summary && <p className="text-xs text-green-600 mt-0.5">{summary}</p>}
        </div>
      </div>
      {isCompleted && onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-xs font-medium text-green-700 hover:text-green-900 px-3 py-1 rounded-lg hover:bg-green-100 transition-colors"
        >
          Editar
        </button>
      )}
    </div>
  )
}

export default function BookCheckoutForm({ userEmail, userName, userPhone, userDocument }: BookCheckoutFormProps) {
  const [step, setStep] = useState<FormStep>('form')
  const [errorMsg, setErrorMsg] = useState('')

  // Section state
  const [activeSection, setActiveSection] = useState<CheckoutSection>('personal')
  const [completedSections, setCompletedSections] = useState<Set<CheckoutSection>>(new Set())
  const router = useRouter()

  // Dados pessoais (pre-populados do perfil)
  const [name, setName] = useState(userName || '')
  const [email, setEmail] = useState(userEmail || '')
  const [cpf, setCpf] = useState(() => {
    if (!userDocument) return ''
    const d = userDocument.replace(/\D/g, '')
    if (d.length === 11) return d.slice(0, 3) + '.' + d.slice(3, 6) + '.' + d.slice(6, 9) + '-' + d.slice(9)
    return userDocument
  })
  const [phone, setPhone] = useState(() => {
    if (!userPhone) return ''
    const d = userPhone.replace(/\D/g, '')
    if (d.length === 11) return '(' + d.slice(0, 2) + ') ' + d.slice(2, 7) + '-' + d.slice(7)
    if (d.length === 10) return '(' + d.slice(0, 2) + ') ' + d.slice(2, 6) + '-' + d.slice(6)
    return userPhone
  })

  // Endereco
  const [cep, setCep] = useState('')
  const [street, setStreet] = useState('')
  const [number, setNumber] = useState('')
  const [complement, setComplement] = useState('')
  const [neighborhood, setNeighborhood] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [loadingCep, setLoadingCep] = useState(false)
  const [cepError, setCepError] = useState('')
  const [addressRevealed, setAddressRevealed] = useState(false)


  // Auto-detect se dados pessoais ja estao preenchidos
  useEffect(() => {
    if (userName && userDocument && userPhone) {
      const cpfDigits = userDocument.replace(/\D/g, '')
      const phoneDigits = userPhone.replace(/\D/g, '')
      if (cpfDigits.length === 11 && phoneDigits.length >= 10) {
        // Dados completos — auto-avanca para endereco
        setCompletedSections(new Set(['personal']))
        setActiveSection('address')
      }
    }
  }, [userName, userDocument, userPhone])

  // Mascara CPF: 000.000.000-00
  const handleCpf = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 11)
    let masked = digits
    if (digits.length > 3) masked = digits.slice(0, 3) + '.' + digits.slice(3)
    if (digits.length > 6) masked = masked.slice(0, 7) + '.' + digits.slice(6)
    if (digits.length > 9) masked = masked.slice(0, 11) + '-' + digits.slice(9)
    setCpf(masked)
  }

  // Mascara CEP: 00000-000 + auto-lookup
  const handleCep = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 8)
    if (digits.length > 5) {
      setCep(digits.slice(0, 5) + '-' + digits.slice(5))
    } else {
      setCep(digits)
    }
    setCepError('')
    if (digits.length === 8) {
      lookupCep(digits)
    }
  }

  // Busca endereco via ViaCEP
  const lookupCep = useCallback(async (cepValue: string) => {
    const digits = cepValue.replace(/\D/g, '')
    if (digits.length !== 8) return

    setLoadingCep(true)
    setCepError('')
    setAddressRevealed(false)
    try {
      const res = await fetch('https://viacep.com.br/ws/' + digits + '/json/')
      const data = (await res.json()) as Record<string, string> & { erro?: boolean }
      if (data.erro) {
        setCepError('CEP não encontrado. Verifique e tente novamente.')
        return
      }
      setStreet(data.logradouro || '')
      setNeighborhood(data.bairro || '')
      setCity(data.localidade || '')
      setState(data.uf || '')
      setTimeout(() => setAddressRevealed(true), 100)
    } catch {
      setCepError('Erro ao buscar CEP. Tente novamente.')
    } finally {
      setLoadingCep(false)
    }
  }, [])

  // Mascara telefone: (00) 00000-0000
  const handlePhone = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 11)
    let masked = digits
    if (digits.length > 0) masked = '(' + digits
    if (digits.length > 2) masked = '(' + digits.slice(0, 2) + ') ' + digits.slice(2)
    if (digits.length > 7) masked = '(' + digits.slice(0, 2) + ') ' + digits.slice(2, 7) + '-' + digits.slice(7)
    setPhone(masked)
  }

  // Validar email
  const isEmailValid = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)

  // Confirmar secao de dados pessoais
  const confirmPersonal = () => {
    if (!name.trim() || !isEmailValid(email) || cpf.replace(/\D/g, '').length !== 11 || phone.replace(/\D/g, '').length < 10) return
    const next = new Set(completedSections)
    next.add('personal')
    setCompletedSections(next)
    setActiveSection('address')
  }

  // Confirmar secao de endereco
  const confirmAddress = () => {
    if (!cep || !street || !number || !neighborhood || !city || !state) return
    const next = new Set(completedSections)
    next.add('address')
    setCompletedSections(next)
    setActiveSection('payment')
  }

  // Editar secao
  const editSection = (section: CheckoutSection) => {
    const next = new Set(completedSections)
    if (section === 'personal') {
      next.delete('personal')
      next.delete('address')
      next.delete('payment')
    } else if (section === 'address') {
      next.delete('address')
      next.delete('payment')
    }
    setCompletedSections(next)
    setActiveSection(section)
  }

  // Resumo de dados pessoais
  const personalSummary = name + ' · ' + email

  // Resumo de endereco
  const addressSummary = street + ', ' + number + (complement ? ' - ' + complement : '') + ' · ' + city + '/' + state

  // Tela: Erro
  if (step === 'error') {
    return (
      <div className="bg-white rounded-2xl border-2 border-red-200 p-8 text-center max-w-lg mx-auto animate-scale-in">
        <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-stone-900 mb-3">Erro no processamento</h3>
        <p className="text-sm text-red-600 mb-6">{errorMsg}</p>
        <button
          onClick={() => setStep('form')}
          className="px-6 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200 transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  // Formulario Principal
  const isPersonalValid =
    name.trim().length > 0 &&
    isEmailValid(email) &&
    cpf.replace(/\D/g, '').length === 11 &&
    phone.replace(/\D/g, '').length >= 10
  const isAddressValid = cep.replace(/\D/g, '').length === 8 && street && number && neighborhood && city && state

  const isLoggedIn = !!userEmail

  return (
    <div className="max-w-lg mx-auto">
      {/* Step Indicator */}
      <StepIndicator activeSection={activeSection} completedSections={completedSections} />

      {/* Info banner para visitantes */}
      {!isLoggedIn && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 animate-fade-in">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
            <div>
              <p className="text-sm text-blue-800 font-medium">Compre sem precisar criar conta</p>
              <p className="text-xs text-blue-600 mt-1">
                Depois do pagamento, chega no seu e-mail o link para baixar o PDF e o EPUB. O livro físico vai para o endereço que você informar.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* SECAO 1: Dados Pessoais */}
        <div className="animate-fade-in">
          <SectionHeader
            title="Dados pessoais"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            }
            isActive={activeSection === 'personal'}
            isCompleted={completedSections.has('personal')}
            summary={completedSections.has('personal') ? personalSummary : undefined}
            onEdit={completedSections.has('personal') ? () => editSection('personal') : undefined}
          />

          {activeSection === 'personal' && (
            <div className="mt-3 p-4 bg-white rounded-xl border shadow-warm-sm animate-fade-in-up">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Nome completo</label>
                  <input
                    type="text"
                    required
                    placeholder="Seu nome completo"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    readOnly={isLoggedIn}
                    className={`w-full px-4 py-3 border rounded-xl text-sm outline-none transition-shadow ${
                      isLoggedIn
                        ? 'bg-stone-50 text-stone-500 cursor-not-allowed'
                        : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                  {!isLoggedIn && (
                    <p className="text-xs text-stone-400 mt-1">É neste e-mail que chega o link do livro digital</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">CPF</label>
                    <input
                      type="text"
                      required
                      placeholder="000.000.000-00"
                      value={cpf}
                      onChange={(e) => handleCpf(e.target.value)}
                      className="w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">Telefone</label>
                    <input
                      type="text"
                      required
                      placeholder="(00) 00000-0000"
                      value={phone}
                      onChange={(e) => handlePhone(e.target.value)}
                      className="w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                    />
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={confirmPersonal}
                disabled={!isPersonalValid}
                className={`w-full mt-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  isPersonalValid
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20'
                    : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                }`}
              >
                Confirmar dados
              </button>
            </div>
          )}
        </div>

        {/* SECAO 2: Endereco */}
        <div className={completedSections.has('personal') || activeSection === 'address' ? 'animate-fade-in' : ''}>
          <SectionHeader
            title="Endereço de entrega"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
            }
            isActive={activeSection === 'address'}
            isCompleted={completedSections.has('address')}
            summary={completedSections.has('address') ? addressSummary : undefined}
            onEdit={completedSections.has('address') ? () => editSection('address') : undefined}
          />

          {activeSection === 'address' && (
            <div className="mt-3 p-4 bg-white rounded-xl border shadow-warm-sm animate-fade-in-up">
              {/* CEP */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-stone-500 mb-1">CEP</label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    placeholder="00000-000"
                    value={cep}
                    onChange={(e) => handleCep(e.target.value)}
                    className={`w-full px-4 py-4 border-2 rounded-xl text-base font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all ${
                      cepError ? 'border-red-300 bg-red-50' : 'border-blue-200 bg-blue-50/30'
                    }`}
                    autoFocus
                  />
                  {loadingCep && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <div className="animate-spin w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full" />
                    </div>
                  )}
                </div>
                {cepError && <p className="text-xs text-red-500 mt-1.5 animate-fade-in">{cepError}</p>}
                {!cepError && !addressRevealed && !loadingCep && (
                  <p className="text-xs text-stone-400 mt-1.5">Digite o CEP para preencher o endereço automaticamente</p>
                )}
              </div>

              {/* Loading skeleton */}
              {loadingCep && (
                <div className="space-y-3 animate-fade-in">
                  <div className="h-12 bg-stone-100 rounded-xl animate-pulse" />
                  <div className="grid grid-cols-3 gap-2">
                    <div className="h-12 bg-stone-100 rounded-xl animate-pulse" />
                    <div className="h-12 bg-stone-100 rounded-xl animate-pulse col-span-2" />
                  </div>
                  <div className="h-12 bg-stone-100 rounded-xl animate-pulse" />
                </div>
              )}

              {/* Campos de endereco revelados apos ViaCEP */}
              {addressRevealed && !loadingCep && (
                <div className="space-y-3">
                  <div className="animate-fade-in-up stagger-1">
                    <label className="block text-xs font-medium text-stone-500 mb-1">Rua / Avenida</label>
                    <input
                      type="text"
                      required
                      value={street}
                      onChange={(e) => setStreet(e.target.value)}
                      className="w-full px-4 py-3 border rounded-xl text-sm bg-stone-50 text-stone-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2 animate-fade-in-up stagger-2">
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1">Número *</label>
                      <input
                        type="text"
                        required
                        placeholder="No"
                        value={number}
                        onChange={(e) => setNumber(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-blue-50/30"
                        autoFocus
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-stone-500 mb-1">Complemento</label>
                      <input
                        type="text"
                        placeholder="Apto, bloco, etc."
                        value={complement}
                        onChange={(e) => setComplement(e.target.value)}
                        className="w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                  </div>
                  <div className="animate-fade-in-up stagger-3">
                    <label className="block text-xs font-medium text-stone-500 mb-1">Bairro</label>
                    <input
                      type="text"
                      required
                      value={neighborhood}
                      onChange={(e) => setNeighborhood(e.target.value)}
                      className="w-full px-4 py-3 border rounded-xl text-sm bg-stone-50 text-stone-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2 animate-fade-in-up stagger-4">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-stone-500 mb-1">Cidade</label>
                      <input
                        type="text"
                        required
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="w-full px-4 py-3 border rounded-xl text-sm bg-stone-50 text-stone-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1">UF</label>
                      <select
                        required
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        className="w-full px-3 py-3 border rounded-xl text-sm bg-stone-50 text-stone-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      >
                        <option value="">UF</option>
                        {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(
                          (uf) => (
                            <option key={uf} value={uf}>
                              {uf}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={confirmAddress}
                    disabled={!isAddressValid}
                    className={`w-full mt-2 py-3 rounded-xl text-sm font-semibold transition-all duration-300 animate-fade-in-up stagger-5 ${
                      isAddressValid
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20'
                        : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                    }`}
                  >
                    Confirmar endereco
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* SECAO 3: Pagamento */}
        <div className={activeSection === 'payment' ? 'animate-fade-in' : ''}>
          <SectionHeader
            title="Pagamento"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
              </svg>
            }
            isActive={activeSection === 'payment'}
            isCompleted={false}
          />

          {activeSection === 'payment' && (
            <div className="mt-3 animate-scale-in">
              <TransparentCheckout
                productType="book"
                productId="livro-ape"
                amount={9790}
                channelSlug="livro"
                title="Livro Metodologia APE"
                description="Livro físico + acesso digital"
                maxInstallments={3}
                allowBoleto={true}
                hideCustomerForm={true}
                buyerName={name}
                buyerEmail={email}
                buyerCpf={cpf.replace(/\D/g, '')}
                buyerPhone={phone.replace(/\D/g, '')}
                prefilledAddress={{
                  street,
                  number,
                  complement,
                  neighborhood,
                  city,
                  state,
                  zipCode: cep.replace(/\D/g, ''),
                  recipientName: name,
                }}
                onSuccess={(paymentId) => router.push('/livro/obrigado?payment=' + paymentId)}
                onError={(error) => {
                  setErrorMsg(error)
                  setStep('error')
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Trust Signals */}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-xs text-stone-400">
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
          <span>Pagamento 100% seguro</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
          </svg>
          <span>Dados protegidos</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
          </svg>
          <span>Físico + PDF e EPUB</span>
        </div>
      </div>
    </div>
  )
}
